import React, { useState, useEffect } from "react";
import { User, MessageSquare, Bot, Package, MapPin, Apple } from "lucide-react";
import { signInWithPopup, OAuthProvider } from "firebase/auth";
import {
  collection,
  onSnapshot,
  query,
  where,
  doc,
  setDoc,
} from "firebase/firestore";
import { db, auth, googleProvider } from "./firebase";
import { useAppContext } from "./context/AppContext";

// СТОРІНКИ ДОДАТКУ
import Profile from "./pages/Profile";
import Chat from "./pages/Chat";
import AiVet from "./pages/AiVet";
import Subs from "./pages/Subs";
import Locations from "./pages/Locations";
import PublicProfile from "./pages/PublicProfile";

const appleProvider = new OAuthProvider("apple.com");

export default function MyPaws() {
  const { lang, setLang, t, isDark, user, loading } = useAppContext();

  const [activeTab, setActiveTab] = useState<string>("profile");
  const [reminders, setReminders] = useState<any[]>([]);
  const [points, setPoints] = useState<number>(0);
  const [unreadDmCount, setUnreadDmCount] = useState(0);

  const [scanId, setScanId] = useState<string | null>(() => {
    return new URLSearchParams(window.location.search).get("scan");
  });

  useEffect(() => {
    if (!user) return;

    const unsubDm = onSnapshot(
      query(
        collection(db, "direct_messages"),
        where("recipientId", "==", user.uid),
        where("read", "==", false)
      ),
      (snap) => {
        setUnreadDmCount(snap.docs.length);
      }
    );

    const unsubUser = onSnapshot(doc(db, "users", user.uid), (docSnap) => {
      if (docSnap.exists()) {
        const userData = docSnap.data();
        if (userData.points !== undefined) {
          setPoints(userData.points);
        } else {
          setDoc(doc(db, "users", user.uid), { points: 0 }, { merge: true });
        }
      }
    });

    const unsubReminders = onSnapshot(
      collection(db, "users", user.uid, "reminders"),
      (snap) => {
        setReminders(snap.docs.map((d) => d.data() as any));
      }
    );

    return () => {
      unsubDm();
      unsubUser();
      unsubReminders();
    };
  }, [user]);

  const handleLoginGoogle = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (e) {}
  };
  const handleLoginApple = async () => {
    try {
      await signInWithPopup(auth, appleProvider);
    } catch (e) {}
  };

  if (scanId) {
    return (
      <div className={`w-full min-h-screen ${isDark ? "dark" : ""}`}>
        <PublicProfile tagId={scanId} />
      </div>
    );
  }

  if (!lang)
    return (
      <div
        className={`h-screen flex flex-col items-center justify-center p-6 bg-[#fffdfd] text-[#2d1f45]`}
      >
        <img
          src="/app_icon.png"
          alt="MyPaws"
          className="w-32 h-32 mb-10 shadow-2xl rounded-[2.5rem]"
        />
        <h1 className="text-2xl font-black mb-8 text-center">
          Choose language
          <br />
          Оберіть мову
        </h1>
        <div className="w-full max-w-xs flex flex-col gap-4">
          <button
            onClick={() => setLang("en")}
            className="w-full h-14 bg-[#2d1f45] text-white rounded-2xl font-bold shadow-lg active:scale-95 transition-all text-lg"
          >
            English
          </button>
          <button
            onClick={() => setLang("uk")}
            className="w-full h-14 bg-white border-2 border-[#e8dff8] text-[#2d1f45] rounded-2xl font-bold shadow-sm active:scale-95 transition-all text-lg"
          >
            Українська
          </button>
        </div>
      </div>
    );

  if (loading)
    return (
      <div className="h-screen flex flex-col items-center justify-center text-[var(--text-main)] font-bold bg-[var(--bg-soft)]">
        <img
          src="/paw-purple.png"
          alt="Paw"
          className="w-16 h-16 mb-4 animate-pulse opacity-80"
        />
        MyPaws...
      </div>
    );

  if (!user)
    return (
      <div className="h-screen flex flex-col items-center justify-center p-6 bg-[var(--bg-soft)] text-center">
        <img
          src="/app_icon.png"
          alt="MyPaws"
          className="w-32 h-32 mb-10 shadow-2xl rounded-[2.5rem]"
        />
        <h1 className="text-3xl font-black text-[var(--text-main)] mb-2">
          MyPaws
        </h1>
        <div className="w-full max-w-xs flex flex-col gap-3">
          <button
            onClick={handleLoginGoogle}
            className="w-full h-14 bg-[var(--bg-card)] border-2 border-[var(--border-color)] text-[var(--text-main)] rounded-2xl flex items-center justify-center gap-3 font-bold shadow-sm active:scale-95 transition-all"
          >
            <img
              src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
              width="20"
              alt="G"
            />{" "}
            {t.loginGoogle}
          </button>
          <button
            onClick={handleLoginApple}
            className="w-full h-14 bg-black text-white border-2 border-black rounded-2xl flex items-center justify-center gap-3 font-bold shadow-sm active:scale-95 transition-all"
          >
            <Apple size={22} className="text-white" fill="currentColor" />{" "}
            {t.loginApple}
          </button>
        </div>
      </div>
    );

  return (
    <div
      className={`w-full min-h-screen bg-[var(--bg-main)] text-[var(--text-main)] relative select-none ${
        isDark ? "dark" : ""
      }`}
    >
      <div className="pb-24">
        {activeTab === "profile" && (
          <Profile
            reminders={reminders}
            points={points}
            setPoints={setPoints}
          />
        )}
        {activeTab === "chat" && <Chat />}
        {activeTab === "aivet" && <AiVet />}
        {activeTab === "subs" && <Subs />}
        {activeTab === "locations" && <Locations />}
      </div>

      {/* Оновлена навігація */}
      <div className="fixed bottom-0 left-0 right-0 bg-[var(--bg-card)] border-t border-[var(--border-color)] z-[100] pb-8 pt-2 h-[80px]">
        <div className="flex justify-around items-center px-4 max-w-md mx-auto">
          {[
            { id: "chat", icon: MessageSquare, label: t.chat },
            { id: "aivet", icon: Bot, label: t.aiVet },
            { id: "profile", icon: User, label: t.profile },
            { id: "subs", icon: Package, label: t.subs },
            { id: "locations", icon: MapPin, label: t.locs },
          ].map((tab) => (
            <div
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-center cursor-pointer transition-all ${
                activeTab === tab.id
                  ? "text-[#a073e7] scale-110"
                  : "text-[var(--text-muted)] opacity-50 relative"
              } ${tab.id === "profile" ? "scale-125 font-black z-10" : ""}`}
            >
              <tab.icon
                size={tab.id === "profile" ? 28 : 24}
                strokeWidth={activeTab === tab.id ? 2.5 : 2}
              />
              {tab.id === "chat" && unreadDmCount > 0 && (
                <span className="absolute -top-1 right-2 flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--red-text)] opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-[var(--red-text)]"></span>
                </span>
              )}
              <span className="text-[10px] font-bold mt-1">{tab.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
